import React from 'react';
import { Stethoscope, Flame, ShieldAlert } from 'lucide-react';
import { getChilePrimaryButtons } from '../../data/sosNumbers';

/**
 * @component SOSStickyBar
 * @description Barra sticky móvil con botones de emergencia de Chile (131/132/133)
 * CRÍTICO: Este componente puede salvar vidas - mantener simple y confiable
 */

interface SOSStickyBarProps {
  /** Función para tracking de analytics */
  onSOSClick?: (service: string, country: string, location: string) => void;
  /** Ocultar la barra en páginas específicas */
  hidden?: boolean;
}

const SOSStickyBar: React.FC<SOSStickyBarProps> = ({ 
  onSOSClick,
  hidden = false 
}) => {
  const chilePrimaryButtons = getChilePrimaryButtons();

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'Stethoscope':
        return <Stethoscope className="h-5 w-5" />;
      case 'Flame':
        return <Flame className="h-5 w-5" />;
      case 'ShieldAlert':
        return <ShieldAlert className="h-5 w-5" />;
      default:
        return <ShieldAlert className="h-5 w-5" />;
    }
  };

  const getButtonColor = (iconName: string) => {
    switch (iconName) {
      case 'Stethoscope':
        return 'bg-red-600 hover:bg-red-700 focus:ring-red-500';
      case 'Flame':
        return 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500';
      case 'ShieldAlert':
        return 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500';
      default:
        return 'bg-red-600 hover:bg-red-700 focus:ring-red-500';
    }
  };

  const handleSOSClick = (service: any) => {
    // Analytics tracking
    if (onSOSClick) {
      onSOSClick(service.numero, 'CL', 'sticky');
    }

    // Abrir marcador telefónico
    window.location.href = service.tel;
  };

  if (hidden) {
    return null;
  }

  return (
    <>
      {/* Barra sticky móvil */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg md:hidden">
        <div className="px-4 py-3">
          {/* Disclaimer legal */}
          <div className="text-xs text-gray-600 text-center mb-2">
            NFCORES no presta atención médica ni policial. Las llamadas se dirigen a servicios oficiales.
          </div>
          
          {/* Título */}
          <div className="text-sm font-semibold text-gray-900 text-center mb-3">
            Emergencia ahora
          </div>
          
          {/* Botones de emergencia */}
          <div className="grid grid-cols-3 gap-2">
            {chilePrimaryButtons.map((service) => (
              <button
                key={service.numero}
                onClick={() => handleSOSClick(service)}
                className={`
                  ${getButtonColor(service.icono)}
                  text-white rounded-lg p-3 flex flex-col items-center justify-center
                  transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
                  active:scale-95 transform transition-transform
                `}
                aria-label={`Llamar ${service.nombre} ${service.numero}`}
                type="button"
              >
                {getIcon(service.icono)}
                <span className="text-xs font-bold mt-1">{service.numero}</span>
                <span className="text-xs">{service.nombre}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Fallback sin JavaScript - CRÍTICO para emergencias */}
      <noscript>
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-red-600 text-white p-4 md:hidden">
          <div className="text-center">
            <div className="text-sm font-bold mb-2">EMERGENCIAS CHILE</div>
            <div className="flex justify-center space-x-4">
              <a 
                href="tel:131" 
                className="bg-white text-red-600 px-3 py-2 rounded font-bold text-sm"
                aria-label="Llamar SAMU Ambulancia 131"
              >
                131 SAMU
              </a>
              <a 
                href="tel:132" 
                className="bg-white text-red-600 px-3 py-2 rounded font-bold text-sm"
                aria-label="Llamar Bomberos 132"
              >
                132 Bomberos
              </a>
              <a 
                href="tel:133" 
                className="bg-white text-red-600 px-3 py-2 rounded font-bold text-sm"
                aria-label="Llamar Carabineros 133"
              >
                133 Carabineros
              </a>
            </div>
            <div className="text-xs mt-2">
              <a href="/sos" className="underline">Más números de emergencia</a>
            </div>
          </div>
        </div>
      </noscript>

    </>
  );
};

export default SOSStickyBar;
